// Configuração da API
const getBaseUrl = () => {
  // Em build de produção (NODE_ENV === 'production'):
  if (process.env.NODE_ENV === 'production') {
    // Se foi fornecida uma URL remota explícita que NÃO contenha localhost:
    if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
      return process.env.REACT_APP_API_URL;
    }
    // Padrão seguro para produção: caminho relativo '/api'
    // Elimina erros de CORS, bloqueio de loopback/private network e problemas com www/non-www
    return '/api';
  }

  // Em desenvolvimento local (npm start):
  return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
};

const API_CONFIG = {
  // URL base da API
  BASE_URL: getBaseUrl(),
  TIMEOUT: 10000, // 10 segundos
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 segundo
};

// Headers padrão para todas as requisições
const getDefaultHeaders = (customHeaders = {}, isFormData = false) => {
  const token = localStorage.getItem('auth_token');
  
  const headers = {
    'Accept': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...customHeaders
  };
  
  // Só definir Content-Type se não for FormData
  if (!isFormData && !customHeaders['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

// Função para fazer requisições com retry automático
const makeRequest = async (url, options = {}) => {
  // Mapear rotas com fallback para ambientes onde o rewrite do host não funciona
  // Em produção, alguns hosts não roteiam "/api/login" para index.php; usar login.php diretamente
  const normalizedUrl = (url === '/login') ? '/login.php' : url;
  const fullUrl = normalizedUrl.startsWith('http') ? normalizedUrl : `${API_CONFIG.BASE_URL}${normalizedUrl}`;
  
  // Detectar se é FormData
  const isFormData = options.body instanceof FormData;
  
  // Preparar headers considerando FormData
  const customHeaders = options.headers || {};
  const headers = getDefaultHeaders(customHeaders, isFormData);
  
  // Preparar body
  let body = options.body;
  if (body && !isFormData && typeof body === 'object') {
    body = JSON.stringify(body);
  }

  const requestOptions = {
    method: options.method || 'GET',
    headers,
    body,
    timeout: API_CONFIG.TIMEOUT,
  };

  let lastError;
  
  for (let attempt = 1; attempt <= API_CONFIG.RETRY_ATTEMPTS; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
      
      const response = await fetch(fullUrl, {
        ...requestOptions,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Caso especial: login com credenciais incorretas
        if (response.status === 401 && (normalizedUrl.endsWith('/login') || normalizedUrl.endsWith('/login.php'))) {
          try {
            const data = await response.json();
            return data; // retorna para o caller tratar mensagem amigável
          } catch (_) {
            return { success: false, message: 'Usuário ou senha incorretos' };
          }
        }

        if (response.status === 401) {
          // Token inválido em outras rotas: derruba sessão
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          window.location.href = '/login';
          throw new Error('Sessão expirada');
        }
        
        // Tentar ler mensagem de erro da resposta JSON
        let errorMessage = `Erro ${response.status}: ${response.statusText}`;
        let errorData = {};
        
        try {
          errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Se não conseguir ler JSON, usar mensagem padrão
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        
        // Tratar erros de validação (422) e outros erros com mensagem
        if (response.status === 422 || response.status === 400) {
          error.errors = errorData.errors || errorData.message || errorMessage;
        }
        
        throw error;
      }

      return response.json();
    } catch (error) {
      lastError = error;
      
      if (attempt < API_CONFIG.RETRY_ATTEMPTS) {
        // Aguardar antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY * attempt));
      }
    }
  }
  
  throw lastError;
};

// Função específica para requisições autenticadas (alias para makeRequest)
const makeAuthenticatedRequest = makeRequest;

export { API_CONFIG, makeRequest, makeAuthenticatedRequest };
